import { API_BASE, apiFetch, isNative, setToken } from "./api";
import { EngramNative } from "./native";

const SIGNED_IN = "engram.signedIn";

export function wasSignedIn(): boolean {
  try {
    return localStorage.getItem(SIGNED_IN) === "1";
  } catch {
    return false;
  }
}

function markSignedIn(v: boolean) {
  try {
    if (v) localStorage.setItem(SIGNED_IN, "1");
    else localStorage.removeItem(SIGNED_IN);
  } catch {
    /* storage unavailable */
  }
}

const LAST_SIGN_OUT = "engram.lastSignOut";

/** Why this device last showed the sign-in screen (diagnostics, shown in Devices & tokens). */
export interface SignOutRecord {
  at: number;
  /** HTTP status, or 0 when the server couldn't be reached. */
  status: number;
  /** Server's reason (no-cookie, unknown-token, revoked) or the app's own (signed-out, offline-first-run). */
  reason: string;
}

export function lastSignOut(): SignOutRecord | null {
  try {
    return JSON.parse(localStorage.getItem(LAST_SIGN_OUT) ?? "null") as SignOutRecord | null;
  } catch {
    return null;
  }
}

export function recordSignOut(status: number, reason: string) {
  try {
    localStorage.setItem(LAST_SIGN_OUT, JSON.stringify({ at: Date.now(), status, reason } satisfies SignOutRecord));
  } catch {
    /* storage unavailable */
  }
}

/** On Android the device token lives in native encrypted storage. */
export async function loadNativeToken(): Promise<string | undefined> {
  if (!isNative) return undefined;
  const { token } = await EngramNative.getToken().catch(() => ({ token: undefined }));
  setToken(token);
  return token;
}

/**
 * "ok" = session valid, "no" = must sign in, "offline" = server unreachable.
 * A 401 is asked again once before signing out, so a single bad answer can't drop a signed-in device.
 */
export async function checkSession(): Promise<"ok" | "no" | "offline"> {
  for (let attempt = 0; ; attempt++) {
    let r: Response;
    try {
      r = await apiFetch("/auth/me");
    } catch {
      return "offline";
    }
    if (r.ok) {
      markSignedIn(true);
      return "ok";
    }
    if (r.status !== 401) return "offline";
    // A device that was signed in asks twice before giving up (a cookie can arrive late after a restart); a device
    // that never was gets the sign-in screen at once.
    if (attempt === 0 && wasSignedIn()) {
      await new Promise((ok) => setTimeout(ok, 1500));
      continue;
    }
    const body = (await r.json().catch(() => ({}))) as { reason?: string };
    recordSignOut(401, body.reason ?? "unauthorized");
    markSignedIn(false); // revoked or lost: the next start shows sign-in first
    return "no";
  }
}

export async function login(password: string): Promise<string | null> {
  const r = await apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ password, device: deviceName(), mode: isNative ? "bearer" : "cookie" }),
  }).catch(() => null);
  if (!r) return "Can't reach the server.";
  if (r.ok) {
    if (isNative) {
      const { token } = (await r.json()) as { token: string };
      setToken(token);
      await EngramNative.setToken({ token, apiBase: API_BASE });
    }
    markSignedIn(true);
    return null;
  }
  const body = (await r.json().catch(() => ({}))) as { error?: string };
  return body.error === "wrong password" ? "Wrong password." : (body.error ?? `Sign-in failed (${r.status}).`);
}

const GOOGLE_OFFERED = "engram.googleOffered";

/**
 * What the sign-in screen offers besides the password: Google once its OAuth client is set up. (Passkeys left the
 * screen in chat 14, owner: "replace passkey with login with Google"; the server keeps their routes, unused.)
 */
export async function signInOptions(): Promise<{ google: boolean }> {
  const r = await apiFetch("/health").catch(() => null);
  if (!r?.ok) return { google: false };
  const h = (await r.json().catch(() => ({}))) as { google?: boolean };
  try {
    localStorage.setItem(GOOGLE_OFFERED, h.google === true ? "1" : "0");
  } catch {
    /* storage blocked */
  }
  return { google: h.google === true };
}

/**
 * What the sign-in screen draws before the server answers: the last answer, else Google (production has it). Drawing
 * the button only when the answer came grew the card (phone first visit CLS 0.021).
 */
export function lastSignInOptions(): { google: boolean } {
  try {
    return { google: localStorage.getItem(GOOGLE_OFFERED) !== "0" };
  } catch {
    return { google: true };
  }
}

/** URL that starts Google sign-in. The web app navigates to it; the Android app opens it in a Custom Tab. */
export function googleStartUrl(): string {
  const q = new URLSearchParams({ platform: isNative ? "app" : "web", device: deviceName() });
  return `${API_BASE}/api/auth/google?${q}`;
}

/** Android: trades the one-time code from engram://auth?code=… for this device's token. */
export async function finishGoogleSignIn(code: string): Promise<string | null> {
  const r = await apiFetch("/auth/google/exchange", { method: "POST", body: JSON.stringify({ code }) }).catch(() => null);
  if (!r) return "Can't reach the server.";
  if (!r.ok) return "That sign-in expired. Try again.";
  const { token } = (await r.json()) as { token: string };
  setToken(token);
  await EngramNative.setToken({ token, apiBase: API_BASE });
  markSignedIn(true);
  return null;
}

export const GOOGLE_ERRORS: Record<string, string> = {
  "not-set-up": "Google sign-in isn't set up yet. Use your password.",
  cancelled: "Google sign-in was cancelled.",
  expired: "That sign-in took too long. Try again.",
  "wrong-account": "That Google account can't sign in to this Engram.",
  failed: "Google sign-in didn't work. Try again, or use your password.",
  locked: "Too many attempts. Try again in a few minutes.",
};

export async function logout() {
  recordSignOut(0, "signed-out");
  await apiFetch("/auth/logout", { method: "POST" }).catch(() => null);
  if (isNative) await EngramNative.setToken({ token: null }).catch(() => undefined);
  setToken(undefined);
  markSignedIn(false);
}

/*
 * What Devices & tokens calls this device until it's renamed (owner, 2026-10-11: "Chrome on Windows means nothing"):
 * "Chrome 155 · Windows 11 · 2560×1440", "Chrome 155 · Galaxy Z Fold7 (SM-F966N)", "Engram app · Galaxy Z Fold7
 * (SM-F966N)". A page can't learn a PC's own name or model; Chrome's client hints give a phone's model and the
 * Windows version (the plain user-agent string hides both), so those are read once at startup.
 */
const MODELS: [RegExp, string][] = [
  [/^SM-F966/, "Galaxy Z Fold7"],
  [/^SM-F956/, "Galaxy Z Fold6"],
  [/^SM-F946/, "Galaxy Z Fold5"],
  [/^SM-F766/, "Galaxy Z Flip7"],
  [/^SM-S938/, "Galaxy S25 Ultra"],
  [/^SM-S936/, "Galaxy S25+"],
  [/^SM-S931/, "Galaxy S25"],
];
const modelName = (m: string) => {
  const known = MODELS.find(([re]) => re.test(m))?.[1];
  return known ? `${known} (${m})` : m;
};

function describeDevice(hints: { model?: string; platformVersion?: string } = {}): string {
  const ua = navigator.userAgent;
  // The app's WebView keeps the model in its user-agent string; Chrome reduces it to "K" and says it in hints.
  const uaModel = ua.match(/Android [\d.]+; ([^;)]+?)(?: Build\/[^;)]*)?[;)]/)?.[1];
  const model = hints.model || (uaModel && uaModel !== "K" ? uaModel : "");
  if (isNative) return `Engram app · ${model ? modelName(model) : "Android"}`;
  const v = (re: RegExp) => ua.match(re)?.[1];
  // The Claude desktop app's built-in browser says "Chrome" too; name it, so its sign-ins aren't mistaken for Chrome's.
  const browser = /Claude\//.test(ua)
    ? "Claude app browser"
    : v(/Edg\/(\d+)/)
      ? `Edge ${v(/Edg\/(\d+)/)}`
      : v(/Chrome\/(\d+)/)
        ? `Chrome ${v(/Chrome\/(\d+)/)}`
        : v(/Firefox\/(\d+)/)
          ? `Firefox ${v(/Firefox\/(\d+)/)}`
          : v(/Version\/(\d+).*Safari/)
            ? `Safari ${v(/Version\/(\d+).*Safari/)}`
            : "Browser";
  const major = Number(hints.platformVersion?.split(".")[0] ?? NaN);
  const os = /Android/.test(ua)
    ? model ? modelName(model) : "Android"
    : /Windows/.test(ua)
      ? Number.isFinite(major) ? (major >= 13 ? "Windows 11" : "Windows 10") : "Windows"
      : /iPhone|iPad/.test(ua)
        ? /iPad/.test(ua) ? "iPad" : "iPhone"
        : /Mac/.test(ua)
          ? "Mac"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  // A PC's screen in real pixels tells two PCs with the same browser apart; a phone is told by its model.
  const [w, h] = [Math.round(screen.width * devicePixelRatio), Math.round(screen.height * devicePixelRatio)];
  const screenSize = /Android|iPhone|iPad/.test(ua) || !w || !h ? "" : `${w}×${h}`;
  return [browser, os, screenSize].filter(Boolean).join(" · ");
}

let described = describeDevice();
type HintsApi = { getHighEntropyValues(h: string[]): Promise<{ model?: string; platformVersion?: string }> };
void (navigator as Navigator & { userAgentData?: HintsApi }).userAgentData
  ?.getHighEntropyValues(["model", "platformVersion"])
  .then((h) => (described = describeDevice(h)))
  .catch(() => undefined);

export function deviceName(): string {
  return described;
}
